import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { toast } from '../../toasts';
import { Button, Checkbox, FormField, IdChip, controlClass } from '../../components/EntityList';
import paymentGroupService from '../../services/paymentGroup.service';
import { getError, showCurrency } from '../../utils';
import {
	allocationErrors,
	allocationSummary,
	buildPaymentGroupPayload,
	openDues,
} from '../../utils/paymentGroupAllocation';

const HEADER_CLASS = 'tw:px-2 tw:py-2 tw:text-left tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3';
const CELL_CLASS = 'tw:px-2 tw:py-1.5 tw:text-[13px] tw:text-ink-0 tw:border-t tw:border-border';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

/**
 * "Cobro de varias cuotas" (SHOW_PAYMENT_GROUPS): registers ONE collection —
 * a bank transfer — that covers several dues of the same debt. Each ticked due
 * gets a share (default: its open balance); whatever the transfer exceeds the
 * shares stays unallocated on the group. POST /api/payment-groups.
 */
function PaymentGroupModal({ show, onClose, dues = [], onSaved }) {
	const candidates = useMemo(() => openDues(dues), [dues]);
	const currency = candidates[0]?.debt?.currency || '';

	const [shares, setShares] = useState({});
	const [date, setDate] = useState(new Date());
	const [amount, setAmount] = useState('');
	const [reference, setReference] = useState('');
	const [paymentMethod, setPaymentMethod] = useState('Transferencia');
	const [observations, setObservations] = useState('');
	const [errors, setErrors] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!show) return;
		setShares({});
		setDate(new Date());
		setAmount('');
		setReference('');
		setPaymentMethod('Transferencia');
		setObservations('');
		setErrors([]);
	}, [show]);

	const allocations = useMemo(
		() => candidates
			.filter((due) => shares[due._id] !== undefined)
			.map((due) => ({ debtDue: due._id, amount: shares[due._id] })),
		[candidates, shares],
	);
	const summary = allocationSummary({ allocations, amount });

	const toggleDue = (due) => {
		setShares((prev) => {
			const next = { ...prev };
			if (next[due._id] !== undefined) delete next[due._id];
			else next[due._id] = String(due.unpaidAmount ?? '');
			return next;
		});
		setErrors([]);
	};

	const setShare = (due, value) => {
		setShares((prev) => ({ ...prev, [due._id]: value }));
		setErrors([]);
	};

	const handleSubmit = async () => {
		if (saving) return;
		const found = allocationErrors({ allocations, dues: candidates, amount, date });
		if (found.length) {
			setErrors(found);
			return;
		}
		setSaving(true);
		try {
			const { data } = await paymentGroupService.create(
				buildPaymentGroupPayload({ date, amount, reference, paymentMethod, observations, allocations }),
			);
			toast.success(data?.message || 'Cobro registrado');
			onSaved?.();
			onClose();
		} catch (error) {
			setErrors([getError(error)]);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal show={show} onHide={onClose} size='lg' centered>
			<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton>
				<Modal.Title className='tw:text-[15px] tw:font-semibold'>Cobro de varias cuotas</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:bg-surface tw:px-5 tw:py-5 tw:flex tw:flex-col tw:gap-4'>
				<p className='tw:m-0 tw:text-[12.5px] tw:text-ink-2'>
					Una sola transferencia que cubre varias cuotas. Marcá las cuotas y ajustá el monto de cada una si hace falta.
				</p>

				{errors.length > 0 && (
					<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
						{errors.map((message) => (
							<div key={message}>{message}</div>
						))}
					</div>
				)}

				<div>
					<div className='tw:mb-1.5 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Cuotas</div>
					{candidates.length === 0 ? (
						<p className='tw:m-0 tw:text-[13px] tw:text-ink-3'>No hay cuotas con saldo pendiente.</p>
					) : (
						<table className='tw:w-full tw:border-collapse tw:tabular-nums'>
							<thead>
								<tr>
									<th className={HEADER_CLASS} />
									<th className={HEADER_CLASS}>ID</th>
									<th className={HEADER_CLASS}>Vencimiento</th>
									<th className={`${HEADER_CLASS} tw:text-right`}>Saldo</th>
									<th className={`${HEADER_CLASS} tw:text-right`}>Monto a aplicar</th>
								</tr>
							</thead>
							<tbody>
								{candidates.map((due) => {
									const checked = shares[due._id] !== undefined;
									return (
										<tr key={due._id}>
											<td className={CELL_CLASS}>
												<Checkbox
													id={`payment-group-due-${due._id}`}
													label={<span className='tw:sr-only'>Cuota {due.customId}</span>}
													checked={checked}
													onChange={() => toggleDue(due)}
													disabled={saving}
												/>
											</td>
											<td className={CELL_CLASS}><IdChip>{due.customId}</IdChip></td>
											<td className={CELL_CLASS}>{formatDate(due.expiration)}</td>
											<td className={`${CELL_CLASS} tw:text-right tw:font-mono`}>{formatMoney(currency, due.unpaidAmount)}</td>
											<td className={`${CELL_CLASS} tw:text-right`}>
												<Form.Control
													type='number'
													step='any'
													min={0}
													max={due.unpaidAmount}
													className={`${controlClass} tw:ml-auto tw:w-[140px] tw:text-right`}
													value={checked ? shares[due._id] : ''}
													onChange={(event) => setShare(due, event.target.value)}
													disabled={!checked || saving}
													aria-label={`Monto para ${due.customId}`}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
				</div>

				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='payment-group-date' label='Fecha' required>
						<DatePicker
							id='payment-group-date'
							selected={date}
							onChange={(value) => {
								setDate(value);
								setErrors([]);
							}}
							dateFormat='dd-MM-yyyy'
							className={`form-control ${controlClass}`}
							placeholderText='dd-mm-yyyy'
							disabled={saving}
						/>
					</FormField>
					<FormField id='payment-group-amount' label='Monto recibido'>
						<Form.Control
							id='payment-group-amount'
							type='number'
							step='any'
							min={0}
							className={controlClass}
							value={amount}
							onChange={(event) => {
								setAmount(event.target.value);
								setErrors([]);
							}}
							placeholder={summary.allocated ? String(summary.allocated) : ''}
							disabled={saving}
						/>
						<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-3'>Vacío: igual a la suma de las cuotas.</p>
					</FormField>
					<FormField id='payment-group-reference' label='Referencia'>
						<Form.Control
							id='payment-group-reference'
							className={controlClass}
							value={reference}
							onChange={(event) => setReference(event.target.value)}
							placeholder='Nro. de transferencia'
							disabled={saving}
						/>
					</FormField>
					<FormField id='payment-group-method' label='Medio de pago'>
						<Form.Control
							id='payment-group-method'
							className={controlClass}
							value={paymentMethod}
							onChange={(event) => setPaymentMethod(event.target.value)}
							placeholder='Ej: Transferencia'
							disabled={saving}
						/>
					</FormField>
				</div>

				<FormField id='payment-group-observations' label='Observaciones'>
					<Form.Control
						id='payment-group-observations'
						as='textarea'
						rows={2}
						className={`${controlClass} tw:h-auto`}
						value={observations}
						onChange={(event) => setObservations(event.target.value)}
						disabled={saving}
					/>
				</FormField>

				<div className='tw:grid tw:grid-cols-3 tw:gap-3 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-4 tw:py-3'>
					<div>
						<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Recibido</div>
						<div className='tw:font-mono tw:text-[14px] tw:tabular-nums'>{formatMoney(currency, summary.received)}</div>
					</div>
					<div>
						<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Asignado ({allocations.length})</div>
						<div className='tw:font-mono tw:text-[14px] tw:tabular-nums'>{formatMoney(currency, summary.allocated)}</div>
					</div>
					<div>
						<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Sin asignar</div>
						<div className={`tw:font-mono tw:text-[14px] tw:tabular-nums ${summary.overAllocated ? 'tw:text-danger' : ''}`}>
							{formatMoney(currency, summary.unallocated)}
						</div>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
				<Button onClick={onClose} disabled={saving}>Cancelar</Button>
				<Button variant='primary' onClick={handleSubmit} disabled={saving || allocations.length === 0}>
					{saving ? 'Guardando...' : 'Registrar cobro'}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

PaymentGroupModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	dues: PropTypes.array,
	onSaved: PropTypes.func,
};

export default PaymentGroupModal;
